/**
 * Utilitaires pour la manipulation des dates et la conversion de formats
 */

/**
 * Vérifie si une valeur est une instance de Date valide
 * @param value Valeur à vérifier
 * @returns true si la valeur est une Date valide, false sinon
 */
export function isDate(value: any): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

/**
 * Convertit une valeur en Date si c'est une chaîne de caractères
 * @param value Valeur à convertir (Date, chaîne ou undefined)
 * @returns Date si la conversion est possible, undefined sinon
 */
export function toDateIfString(value: string | Date | undefined): Date | undefined {
  if (!value) return undefined;
  if (isDate(value)) return value;
  try {
    return new Date(value);
  } catch (e) {
    console.error('Erreur lors de la conversion en Date:', e);
    return undefined;
  }
}

/**
 * Convertit une valeur en chaîne ISO si c'est une Date
 * @param value Valeur à convertir (Date, chaîne ou undefined)
 * @returns Chaîne ISO si la valeur est une Date, la chaîne d'origine si c'est déjà une chaîne, null sinon
 */
export function toISOStringIfDate(value: Date | string | undefined): string | null {
  if (!value) return null;
  if (isDate(value)) return value.toISOString();
  return typeof value === 'string' ? value : null;
}

/**
 * Convertit un objet avec des propriétés en camelCase vers un objet avec des propriétés en snake_case
 * @param obj Objet à convertir
 * @returns Objet avec des propriétés en snake_case
 */
export function toCamelCase<T = any>(obj: Record<string, any>): T {
  if (typeof obj !== 'object' || obj === null) return obj as unknown as T;
  
  const result: Record<string, any> = {};
  
  Object.keys(obj).forEach(key => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    
    // Conversion récursive pour les objets imbriqués
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key]) && !(obj[key] instanceof Date)) {
      result[camelKey] = toCamelCase(obj[key]);
    } else if (Array.isArray(obj[key])) {
      // Conversion récursive pour les tableaux d'objets
      result[camelKey] = obj[key].map((item: any) => 
        typeof item === 'object' && item !== null ? toCamelCase(item) : item
      );
    } else {
      result[camelKey] = obj[key];
    }
  });
  
  return result as T;
}

/**
 * Convertit un objet avec des propriétés en camelCase vers un objet avec des propriétés en snake_case
 * @param obj Objet à convertir
 * @returns Objet avec des propriétés en snake_case
 */
export function toSnakeCase<T = any>(obj: Record<string, any>): T {
  if (typeof obj !== 'object' || obj === null) return obj as unknown as T;
  
  const result: Record<string, any> = {};
  
  Object.keys(obj).forEach(key => {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    
    // Conversion récursive pour les objets imbriqués
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key]) && !(obj[key] instanceof Date)) {
      result[snakeKey] = toSnakeCase(obj[key]);
    } else if (Array.isArray(obj[key])) {
      // Conversion récursive pour les tableaux d'objets
      result[snakeKey] = obj[key].map((item: any) => 
        typeof item === 'object' && item !== null ? toSnakeCase(item) : item
      );
    } else {
      result[snakeKey] = obj[key];
    }
  });
  
  return result as T;
}

